import React, { useId } from "react";
import { QRCodeSVG } from "qrcode.react";
import { findBelt } from "./cardsData";
import { formatDate } from "../../utils/formatDate";
import { transliterate } from "../../utils/translit";
import logo from "../../assets/mainLogo.webp";
import { fullName } from "../../utils/fullName";

// Every line of text is drawn into a fixed box of the 1000x630 card. If a line is too long for
// its box it is compressed to fit instead of overflowing.
const FitText = ({ x, y, size, maxWidth, weight = 400, fill = "#0f172a", anchor, children }) => {
  const text = String(children ?? "");
  if (!text) return null;
  const tooLong = text.length * size * 0.6 > maxWidth;
  return (
    <text
      x={x}
      y={y}
      fontSize={size}
      fontWeight={weight}
      fill={fill}
      textAnchor={anchor}
      fontFamily="Arial, Helvetica, sans-serif"
      textLength={tooLong ? maxWidth : undefined}
      lengthAdjust={tooLong ? "spacingAndGlyphs" : undefined}
    >
      {text}
    </text>
  );
};

const Label = ({ x = 310, y, children }) => {
  const tooLong = String(children).length * 15 * 0.62 > 300;
  return (
    <text
      x={x}
      y={y}
      fontFamily="Arial, Helvetica, sans-serif"
      fontSize="15"
      fontWeight="700"
      fill="#475569"
      textLength={tooLong ? 300 : undefined}
      lengthAdjust={tooLong ? "spacingAndGlyphs" : undefined}
    >
      {children}
    </text>
  );
};

const Rule = ({ y, x1 = 310, x2 = 650 }) => (
  <line x1={x1} y1={y} x2={x2} y2={y} stroke="#94a3b8" strokeWidth="1.5" />
);

const upper = (text) => String(text ?? "").toLocaleUpperCase("hy");

// Splits a text into at most `maxLines` lines that fit `width` at font `size` (rough glyph width).
const wrap = (text, size, width, maxLines = 3) => {
  const maxChars = Math.max(8, Math.floor(width / (size * 0.58)));
  const lines = [];
  let line = "";
  String(text ?? "")
    .split(" ")
    .filter(Boolean)
    .forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      if (next.length <= maxChars || !line) {
        line = next;
      } else {
        lines.push(line);
        line = word;
      }
    });
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
};

// Text that may take several lines; every line is still squeezed to fit if it is too long.
const Lines = ({ x, y, size, width, step, lines, ...rest }) =>
  lines.map((line, i) => (
    <FitText key={i} x={x} y={y + i * step} size={size} maxWidth={width} {...rest}>
      {line}
    </FitText>
  ));

const cardNumber = (id) => String(id).padStart(6, "0");

// Federation member card, drawn as vector graphics so every word and the photo can be replaced
// by data. The strip at the bottom and the belt picture take the colour of the member's belt.
// `flipped` turns the card over (QR code on the back), `onClick` makes it interactive.
const MemberCard = ({ belt, user, flipped = false, onClick }) => {
  const uid = useId().replace(/:/g, "");
  const template = findBelt(belt);
  if (!template) return null;

  const memberName = fullName(user);
  const trainer = user.trainer;
  const coachName = trainer ? fullName(trainer) : "";
  const placeName = user.place?.name || "";
  const membership = user.membership || { status: "none", valid_until: null };
  const validUntil = formatDate(membership.valid_until);
  const status =
    {
      active: { color: "#15803d", text: "ԳՈՐԾՈՒՄ Է" },
      expired: { color: "#b91c1c", text: "ԺԱՄԿԵՏԱՆՑ" },
      none: { color: "#6b7280", text: "ՉԻ ՎՃԱՐՎԱԾ" },
    }[membership.status] || { color: "#6b7280", text: "" };
  const profileUrl = `${window.location.origin}/judo-users/${user.id}`;

  // Below the first rows school/club and coach each take the whole width (650 units), one after another.
  const WIDE = 650;
  const schoolLines = wrap(placeName, 21, WIDE, 2);
  const coachLines = wrap(coachName, 21, WIDE, 2);
  const schoolLabelY = 336;
  const schoolEnd = schoolLabelY + 33 + (schoolLines.length - 1) * 25;
  const coachLabelY = schoolEnd + 46;
  const coachNameY = coachLabelY + 30;
  const coachEnd = coachNameY + (coachLines.length - 1) * 25;

  return (
    <div
      className={`member-card${flipped ? " flipped" : ""}${onClick ? " interactive" : ""}`}
      onClick={onClick}
    >
      <div className="member-card-inner">
        <div className="member-card-face front">
          <svg viewBox="0 0 1000 630" role="img" aria-label={`Ջուդոիստի քարտ — ${template.label}`}>
            <defs>
              <clipPath id={`photo-${uid}`}>
                <rect x="40" y="180" width="230" height="300" />
              </clipPath>
            </defs>

            {/* Card background */}
            <rect width="1000" height="630" rx="15" ry="15" fill="#f0f2f5" stroke="#d1d5db" strokeWidth="2" />
            <line x1="40" y1="120" x2="960" y2="120" stroke="#1e293b" strokeWidth="3" />

            {/* Header */}
            <image href={logo} x="40" y="14" width="110" height="96" preserveAspectRatio="xMidYMid meet" />
            <text x="170" y="50" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="24" fill="#0f172a">
              ՀԱՅԱՍՏԱՆԻ ՀԱՆՐԱՊԵՏՈՒԹՅԱՆ
            </text>
            <text x="170" y="80" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="24" fill="#0f172a">
              ՋՈՒԴՈՅԻ ՖԵԴԵՐԱՑԻԱ
            </text>
            <text x="170" y="105" fontFamily="Arial, Helvetica, sans-serif" fontSize="16" fill="#0f172a">
              ARMENIAN JUDO FEDERATION
            </text>
            <text x="770" y="60" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="18" fill="#0f172a">
              ՀԱՅԱՍՏԱՆ
            </text>
            <text x="770" y="85" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="18" fill="#0f172a">
              ARMENIA
            </text>
            <g transform="translate(878, 50)">
              <rect width="80" height="15" fill="#d9381e" />
              <rect y="15" width="80" height="15" fill="#003399" />
              <rect y="30" width="80" height="15" fill="#f2a800" />
            </g>

            {/* Photo */}
            <rect x="40" y="180" width="230" height="300" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2" />
            <image
              href={user.image_url}
              x="40"
              y="180"
              width="230"
              height="300"
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#photo-${uid})`}
            />

            {/* Row 1: date of birth and membership */}
            <Label y="166">ԾՆՆԴՅԱՆ ԱՄՍԱԹԻՎ / DATE OF BIRTH</Label>
            <FitText x={310} y={199} size={24} weight={700} maxWidth={300}>
              {formatDate(user.date_of_birth)}
            </FitText>
            <Rule y="214" x2="620" />

            <Label x={650} y="166">ԳՈՐԾՈՒՄ Է ՄԻՆՉԵՎ / VALID UNTIL</Label>
            <FitText x={650} y={199} size={24} weight={700} fill={status.color} maxWidth={170}>
              {validUntil || "—"}
            </FitText>
            <FitText x={840} y={199} size={14} weight={700} fill={status.color} maxWidth={110}>
              {status.text}
            </FitText>
            <Rule y="214" x1="650" x2="960" />

            {/* Row 2: belt, full width with the belt picture at the right end */}
            <Label y="250">ԳՈՏԻ / BELT</Label>
            <FitText x={310} y={283} size={24} weight={700} maxWidth={520}>
              {template.label}
            </FitText>
            <g transform="translate(850, 247)">
              <rect x="0" y="10" width="110" height="20" rx="4" fill={template.color} stroke="#00000040" strokeWidth="1" />
              <polygon points="55,4 76,38 34,38" fill={template.color} stroke="#00000040" strokeWidth="1" />
              <circle cx="55" cy="20" r="7" fill="#00000055" />
            </g>
            <Rule y="298" x2="960" />

            {/* Row 3: school / club, full width */}
            <Label y={schoolLabelY}>ԴՊՐՈՑ / ԱԿՈՒՄԲ / ՖԵԴԵՐԱՑԻԱ</Label>
            <Lines x={310} y={schoolLabelY + 33} size={21} width={WIDE} step={25} lines={schoolLines} />
            <Rule y={schoolEnd + 14} x2="960" />

            {/* Row 4: coach, full width; phone and e-mail share a line */}
            <Label y={coachLabelY}>ՄԱՐԶԻՉ / COACH</Label>
            <Lines x={310} y={coachNameY} size={21} width={WIDE} step={25} lines={coachLines} />
            {trainer?.phone && (
              <FitText x={310} y={coachEnd + 28} size={18} fill="#334155" maxWidth={280}>
                {`Հեռ.՝ ${trainer.phone}`}
              </FitText>
            )}
            {trainer?.email && (
              <FitText x={trainer?.phone ? 620 : 310} y={coachEnd + 28} size={18} fill="#334155" maxWidth={340}>
                {trainer.email}
              </FitText>
            )}

            {/* Bottom strip in the belt colour */}
            <path d="M 0 540 L 1000 540 L 1000 615 Q 1000 630 985 630 L 15 630 Q 0 630 0 615 Z" fill={template.band} />
            <FitText x={40} y={568} size={22} weight={700} fill="#ffffff" maxWidth={250}>
              {upper(memberName)}
            </FitText>
            <FitText x={40} y={591} size={18} weight={700} fill="#ffffff" maxWidth={250}>
              {transliterate(memberName).toUpperCase()}
            </FitText>
            <text x="40" y="613" fontFamily="Arial, Helvetica, sans-serif" fontSize="14" fill="#ffffffcc">
              ՋՈՒԴՈԻՍՏ / JUDO ATHLETE
            </text>

            <line x1="318" y1="556" x2="318" y2="616" stroke="#cbd5e1" strokeWidth="1" />
            <FitText x={340} y={584} size={14} weight={700} fill="#ffffff" maxWidth={520}>
              ՀԱՂԹԱՆԱԿԸ ՍԿՍՎՈՒՄ Է ՀԱՎԱՔԱԿԱՆՈՒԹՅՈՒՆԻՑ
            </FitText>
            <FitText x={340} y={606} size={14} weight={700} fill="#ffffff" maxWidth={520}>
              VICTORY STARTS WITH DISCIPLINE
            </FitText>
            <text
              x="960"
              y="596"
              textAnchor="end"
              fontFamily="Arial, Helvetica, sans-serif"
              fontWeight="700"
              fontSize="24"
              fill="#ffffff"
            >
              JUDO
            </text>
          </svg>
        </div>

        <div className="member-card-face back">
          <div className="mcb-head">
            <strong>ՀԱՅԱՍՏԱՆԻ ՁՈՒԴՈՅԻ ՖԵԴԵՐԱՑԻԱ</strong>
            <span>ARMENIAN JUDO FEDERATION</span>
          </div>
          <div className="mcb-body">
            <div className="mcb-qr">
              <QRCodeSVG value={profileUrl} level="M" bgColor="#ffffff" fgColor="#111111" style={{ width: "100%", height: "100%" }} />
            </div>
            <div className="mcb-info">
              <span className="mcb-name">
                {memberName}
              </span>
              <span>ID № {cardNumber(user.id)}</span>
              <span className="mcb-hint">Սկանավորեք՝ էջը բացելու համար</span>
              <span className="mcb-hint">Scan to open profile</span>
            </div>
          </div>
          <div className="mcb-foot" style={{ background: template.band, color: "#fff" }}>
            VICTORY STARTS WITH DISCIPLINE
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberCard;
